import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faDownload, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import CgDialog from 'modules/Common/ui/CgDialog';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';

// Lightweight, globally-mountable "report ready" confirmation, shown when the
// user clicks Download on a ready full-report job in the background-jobs banner.
//
// Deliberately self-contained: it does NOT navigate to the report page and never
// triggers a render. The parent's `onDownload` fetches the already-rendered PDF
// from its presigned URL and saves it. Mirrors FullReportModal's ready branch so
// the two read alike. Rendered above the banner (z-[1100]) via CgDialog's
// `zIndex` prop so the modal sits on top of the bar.
export default function BannerDownloadModal({
  open,
  onClose,
  onDownload,
  name,
  downloading,
  error,
}) {
  const { t } = useTranslation('app');
  return (
    <CgDialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !downloading) onClose();
      }}
      width={480}
      closeOnOverlayClick={!downloading}
    >
      <div className="flex flex-col items-center text-center gap-4 py-2">
        <div
          className={`w-16 h-16 rounded-full flex items-center justify-center ${
            error ? 'bg-rose-50' : 'bg-primaryColor/10'
          }`}
        >
          <FontAwesomeIcon
            icon={error ? faCircleExclamation : faCheck}
            className={`text-2xl ${error ? 'text-rose-500' : 'text-primaryColor'}`}
          />
        </div>
        <h3 className="text-base font-semibold text-slate-800 m-0">
          {error
            ? t('jobs.downloadFailedTitle', 'Download failed')
            : t('jobs.reportReady', 'Your full report is ready')}
        </h3>
        <p className={`text-sm m-0 ${error ? 'text-rose-500' : 'text-slate-500'}`}>
          {error ||
            name ||
            t('jobs.reportReadySubtitle', 'Reviewed, validated, and compliance-ready.')}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <CgRadixButton
            variant="ghost"
            onClick={onClose}
            disabled={downloading}
          >
            {t('common:actions.close', 'Close')}
          </CgRadixButton>
          <CgRadixButton
            variant="primary"
            icon={faDownload}
            loading={downloading}
            loadingLabel={t('jobs.downloading', 'Downloading…')}
            onClick={onDownload}
          >
            {error ? t('jobs.tryAgain', 'Try again') : t('jobs.downloadReport', 'Download report')}
          </CgRadixButton>
        </div>
      </div>
    </CgDialog>
  );
}
